export type ChartScale = {
  dataMax: number;
  scaleMax: number;
  step: number;
  ticks: number[];
};

const NICE_MULTIPLIERS = [1, 2, 2.5, 3, 4, 5, 10] as const;

const finitePositiveInteger = (value: number): number =>
  Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0;

const niceIntegerStep = (desiredStep: number): number => {
  if (desiredStep <= 1) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(desiredStep));
  const normalized = desiredStep / magnitude;
  const multiplier = NICE_MULTIPLIERS.find((candidate) => candidate >= normalized) ?? 10;
  return Math.max(1, Math.ceil(multiplier * magnitude));
};

const DURATION_STEPS_MINUTES = [1, 5, 10, 15, 30, 60, 90, 120, 180] as const;

const niceDurationStep = (desiredStep: number): number =>
  DURATION_STEPS_MINUTES.find((candidate) => candidate >= desiredStep)
  ?? niceIntegerStep(desiredStep / 60) * 60;

const createScale = (
  values: readonly number[],
  targetIntervals: number,
  stepFor: (desiredStep: number) => number
): ChartScale => {
  const dataMax = Math.max(0, ...values.map(finitePositiveInteger));
  if (dataMax === 0) return { dataMax: 0, scaleMax: 0, step: 0, ticks: [0] };

  const intervals = Number.isFinite(targetIntervals) ? Math.max(3, Math.min(4, Math.round(targetIntervals))) : 4;
  const step = stepFor(dataMax / intervals);
  const scaleMax = Math.ceil(dataMax / step) * step;
  const tickCount = Math.round(scaleMax / step);
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => index * step);

  return { dataMax, scaleMax, step, ticks };
};

export const createChartScale = (values: readonly number[], targetIntervals = 4): ChartScale =>
  createScale(values, targetIntervals, niceIntegerStep);

export const createDurationChartScale = (values: readonly number[], targetIntervals = 4): ChartScale =>
  createScale(values, targetIntervals, niceDurationStep);
